import { Box, Button, Flex, Group, Text } from '@mantine/core';
import { IconCircleCheck, IconDownload, IconFileText, IconFolderPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { PresentedFile } from '../chat/types';
import { SaveToDocumentsModal } from './SaveToDocumentsModal';
import { useSavePresentedFile } from './useSavePresentedFile';

// Where a save files the document, as the server resolved it from the conversation: a client
// conversation into that client's documents, a household conversation into the family's.
export type SaveOwner = { kind: 'client' | 'family'; id: string };

export type PresentedFileSaveContext = {
  conversationId: string;
  owner: SaveOwner;
  // Sandbox-export S3 keys this conversation has already saved to documents. The key is
  // the durable identity behind the card's presigned URL, which is re-signed every load.
  savedSourceKeys?: string[];
};

type Props = {
  file: PresentedFile;
  saveContext?: PresentedFileSaveContext;
};

// Mirrors the backend's key extraction: the path may carry a bucket prefix (LocalStack
// path-style URLs), but the key itself always starts at the sandbox prefix.
function isAlreadySaved(url: string, savedSourceKeys: string[] | undefined): boolean {
  if (!savedSourceKeys?.length) return false;
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const keyStart = path.indexOf('sandbox_exports/');
    if (keyStart === -1) return false;
    return savedSourceKeys.includes(path.slice(keyStart));
  } catch {
    return false;
  }
}

export function PresentedFileCard({ file, saveContext }: Props) {
  const { flagEnabledSiestaSaveToDocuments } = useFlags();
  const [modalOpened, setModalOpened] = useState(false);
  // Covers the just-saved case before the server list refetches; reloads are covered by
  // savedSourceKeys, which the sidebar rehydrates from the server.
  const [savedThisSession, setSavedThisSession] = useState(false);
  const { savePresentedFile, isSaving } = useSavePresentedFile();

  const saved = savedThisSession || isAlreadySaved(file.url, saveContext?.savedSourceKeys);
  const canSave = flagEnabledSiestaSaveToDocuments && !!saveContext;

  const handleSave = async (parentId: string | null) => {
    if (!saveContext) return;
    await savePresentedFile({
      conversationId: saveContext.conversationId,
      url: file.url,
      owner: saveContext.owner,
      parentId,
    });
    setSavedThisSession(true);
  };

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center">
          <IconFileText size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            {file.description}
          </Text>
        </Group>
        <Group gap="sm">
          <Button
            component="a"
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="filled"
            color="orange"
            size="sm"
            leftSection={<IconDownload size={14} />}
          >
            Download
          </Button>
          {canSave && !saved && (
            <Button
              variant="light"
              color="orange"
              size="sm"
              leftSection={<IconFolderPlus size={14} />}
              onClick={() => setModalOpened(true)}
              loading={isSaving}
            >
              Save to documents
            </Button>
          )}
          {saved && (
            <Group gap="xxs" align="center">
              <IconCircleCheck
                size={16}
                stroke={1.5}
                color="var(--mantine-color-success-7)"
                aria-hidden
              />
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="success.8">
                Saved to documents
              </Text>
            </Group>
          )}
        </Group>
      </Flex>
      {canSave && saveContext && (
        <SaveToDocumentsModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          owner={saveContext.owner}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </Box>
  );
}
